'use client';

import { motion } from 'framer-motion';
import { CTA } from '@/components/CTA/CTA';
import MediaCarousel from '@/components/media-carousel/MediaCarousel';
import { cn } from '@/lib/utils';
import { components } from '@/types/strapi';
type TwocolumnCta_type =
	components['schemas']['DynamicZoneTwoColumnCtaComponent'];

interface TwoColumnCTA_ extends TwocolumnCta_type {
	isFirst: boolean;
}
const TwoColumnCTA = ({
	heading,
	content,
	images,
	image_position = 'right',
	background_color = '#ffffff',
	CTAs = [],
	content_color = '#585c66',
	heading_color = '#101827',
	isFirst,
}: TwoColumnCTA_) => {
	return (
		<section
			className={cn(
				'two-column-cta-section px-4 py-12 sm:px-6 lg:px-8 lg:py-20',
			)}
			style={{ backgroundColor: background_color }}>
			<noscript>
				<style>{`
					.two-column-cta-section h1[style],
					.two-column-cta-section h2[style],
					.two-column-cta-section p[style],
					.two-column-cta-section div[style*='opacity'],
					.two-column-cta-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mx-auto max-w-7xl">
				<div
					className={cn(
						'grid grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-16',
						image_position === 'right'
							? 'lg:grid-flow-col'
							: 'lg:grid-flow-col-dense',
					)}>
					{/* Content Column */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6, ease: 'easeOut' }}
						className={cn(
							'flex flex-col space-y-6',
							image_position === 'right'
								? 'md:order-1'
								: 'md:order-2',
						)}>
						{isFirst && (
							<motion.h1
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{
									duration: 0.6,
									delay: 0.2,
									ease: 'easeOut',
								}}
								style={{ color: heading_color }}
								className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
								{heading}
							</motion.h1>
						)}
						{!isFirst && (
							<motion.h2
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{
									duration: 0.6,
									delay: 0.2,
									ease: 'easeOut',
								}}
								style={{ color: heading_color }}
								className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
								{heading}
							</motion.h2>
						)}
						{content && (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{
									duration: 0.6,
									delay: 0.3,
									ease: 'easeOut',
								}}
								style={{ color: content_color }}
								className="prose prose-base prose-gray dark:prose-invert sm:prose-base md:prose-lg prose-headings:font-semibold prose-headings:leading-tight prose-headings:text-gray-800 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-h4:text-base prose-p:leading-relaxed prose-p:tracking-wide prose-p:text-gray-600 prose-a:font-medium prose-a:text-blue-600 prose-a:no-underline prose-a:transition-colors prose-a:duration-200 hover:prose-a:text-blue-800 hover:prose-a:underline prose-blockquote:border-l-4 prose-blockquote:border-l-blue-500 prose-blockquote:py-1 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-500 prose-strong:font-bold prose-strong:text-gray-800 prose-em:italic prose-em:text-gray-700 prose-ol:list-decimal prose-ol:pl-6 prose-ol:text-gray-600 prose-ul:list-disc prose-ul:pl-6 prose-ul:text-gray-600 prose-li:my-2 prose-li:pl-2 prose-table:w-full prose-table:border-collapse prose-th:bg-gray-100 prose-th:p-2 prose-th:text-left prose-th:font-medium prose-td:border-t prose-td:border-gray-200 prose-td:p-2 prose-img:my-6 prose-img:rounded-lg prose-img:shadow-md dark:prose-headings:text-gray-100 dark:prose-p:text-gray-300 dark:prose-a:text-blue-400 dark:prose-strong:text-gray-200 prose-h1:md:text-3xl prose-h2:md:text-2xl prose-h3:md:text-xl prose-h4:md:text-lg max-w-none"
								dangerouslySetInnerHTML={{ __html: content }}
							/>
						)}
						<motion.div
							initial={{ opacity: 0 }}
							whileInView={{ opacity: 1 }}
							viewport={{ once: true }}
							transition={{
								duration: 0.6,
								delay: 0.4,
								ease: 'easeOut',
							}}
							className="mt-8 flex flex-wrap gap-4">
							<CTA CTAs={CTAs} />
						</motion.div>
					</motion.div>

					{/* Image Column */}
					{images && images.length > 0 && (
						<motion.div
							initial={{ opacity: 0, scale: 0.95 }}
							whileInView={{ opacity: 1, scale: 1 }}
							viewport={{ once: true }}
							transition={{ duration: 0.7, ease: 'easeOut' }}
							whileHover={{ scale: 1.02 }}
							className={cn(
								'relative aspect-square overflow-hidden rounded-xl',
								image_position === 'right'
									? 'lg:order-2'
									: 'lg:order-1',
							)}>
							<MediaCarousel images={images} />
						</motion.div>
					)}
				</div>
			</div>
		</section>
	);
};

export default TwoColumnCTA;
